/**
 * Drive 백업: 암호화된 백업 파일을 Google Drive(앱 전용 공간)에 올리고, 거기서 복원한다.
 * 백업 파일은 vault 키로 암호화되어 있어 passphrase 없이는 열 수 없다 (Drive에 평문 없음).
 * 동기화와는 별개인 "그 시점의 사본"이다: 하루 하나, 최근 KEEP개만 남긴다.
 */
import { exportBackup } from '@/data/backup';
import { META, type CounselDb } from '@/data/db/schema';
import type { RemoteFile, RemoteStore } from '@/data/sync/remote';
import { localDate } from '@/domain/workflow/dates';
import { restoreFromBackupFile, type AppSession } from './appSession';
import { driveStore } from './syncService';

export const DRIVE_BACKUP_PREFIX = 'backups/';
const KEEP = 8;
const AUTO_EVERY_MS = 7 * 24 * 60 * 60 * 1000;

export async function backupToDrive(session: AppSession, remote: RemoteStore = driveStore()): Promise<string> {
  const { text } = await exportBackup(session.db, session.ctx.dataKey);
  const name = `${DRIVE_BACKUP_PREFIX}sdcare-${localDate()}.json`;
  await remote.write(name, text);
  const all = (await remote.list(DRIVE_BACKUP_PREFIX)).sort((a, b) => b.name.localeCompare(a.name));
  for (const f of all.slice(KEEP)) await remote.remove(f.name);
  await session.db.meta.put({ key: META.lastDriveBackupAt, value: new Date().toISOString() });
  return name;
}

/** 최근 것부터 */
export async function listDriveBackups(remote: RemoteStore = driveStore()): Promise<RemoteFile[]> {
  return (await remote.list(DRIVE_BACKUP_PREFIX)).sort((a, b) => b.name.localeCompare(a.name));
}

export async function restoreFromDriveBackup(
  name: string,
  passphrase: string,
  remote: RemoteStore = driveStore(),
  db?: CounselDb,
): Promise<AppSession> {
  const text = await remote.read(name);
  if (!text) throw new Error('Drive에서 백업 파일을 찾지 못했습니다');
  return db ? restoreFromBackupFile(text, passphrase, db) : restoreFromBackupFile(text, passphrase);
}

/** 동기화가 끝난 뒤: 마지막 Drive 백업이 7일이 지났으면 자동으로 하나 올린다 (실패해도 동기화에는 영향 없음) */
export async function autoDriveBackup(session: AppSession, remote: RemoteStore = driveStore()): Promise<boolean> {
  const last = (await session.db.meta.get(META.lastDriveBackupAt))?.value as string | undefined;
  if (last && Date.now() - Date.parse(last) < AUTO_EVERY_MS) return false;
  if ((await session.db.entities.count()) === 0) return false;
  await backupToDrive(session, remote);
  return true;
}

/** 'backups/sdcare-2026-10-20.json' → '2026-10-20' */
export const driveBackupDate = (name: string) => /(\d{4}-\d{2}-\d{2})/.exec(name)?.[1] ?? name;
